<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="pdp-story-container">
    <div class="row mb-3">
      <!-- Title section -->
      <section
        id="pdp-head-section"
        class="col-lg-7 col-xl-8"
      >
        <!-- Preview message -->
        <div
          v-if="deal.is_preview"
          class="pdp-preview-box"
        >
          <div class="alert alert-warning my-3">
            <font-awesome-icon
              icon="exclamation-triangle"
              class="mr-3 alert-icon"
            />
            <span class="alert-text">
              This is a preview of your product detail page.
            </span>
          </div>
        </div>

        <div>
          <div class="d-flex justify-content-space-between icon-container-spacing">
            <ListingTypeLogo
              v-if="hasListingTypeLogo"
              class="listing-type-logo-container"
              :public-name="deal.public_name"
              :listing-type="deal.listing_type"
              tooltip-id="mobile-listing-type-tooltip"
            />
            <div
              v-if="showShareModal || showWishlists"
              class="pdp-interaction-container d-lg-none"
            >
              <ShareModal
                v-if="showShareModal"
                class="mr-2 mr-4-sm"
                :shareable-text="shareableLink"
                :title="deal.public_name"
                :subheader="deal.products?.[0].story?.subheader"
                label-text="Or copy link"
                button-class="mobile-share-icon-container"
                icon-class="dark-mode"
                wrapper-class="modal-wishlists"
                :params="socialParams"
              />
              <WishlistsButton
                v-if="showWishlists"
                :deal-id="deal.id"
                :deals-count="wishlistsCount"
              />
            </div>
          </div>
          <h1 class="pdp-page-title">
            {{ deal.public_name }}
          </h1>
          <p v-if="!deal.is_info" class="pdp-plan-types h5">
            {{ planTypeText }}
          </p>
          <h2
            v-if="story.subheader"
            class="pdp-product-subtitle pdp-subtitle mb-1"
          >
            {{ story.subheader }}
          </h2>
          <TacoRating
            v-if="!deal.is_preview && !deal.is_info"
            class="d-lg-none mb-3"
            :deal="deal"
          />
        </div>
      </section>
    </div>

    <div class="pdp-details-wrapper row">
      <section
        id="pdp-carousel-section"
        class="pdp-main-section col-lg-7 col-xl-8 mb-3"
      >
        <ProductsMainImage
          v-if="images.length || story.videos.length"
          :images="images"
          :videos="story.videos"
          class="mb-5"
        />
        <div class="d-lg-none mt-3 mb-5">
          <PDPPricingCard
            :deal="deal"
            :buy-button="buyButton"
            :plan-type-text="planTypeText"
            :subheader="story.subheader"
            :plans="deal.plans"
            :user="user"
            id-suffix="01"
            :is-pricing-card-email-submitted="isPricingCardEmailSubmitted"
            :banner-details="deal.banner_details"
            @email-submitted="onEmailSubmitted"
          />
        </div>
        <div
          v-if="story.snippet"
          class="pdp-description"
          v-html="story.snippet"
        />
        <PDPTldr
          v-if="showTldr"
          class="mt-5"
          :highlights="story.highlights"
        />
        <ProductsAtGlance
          v-if="attributes.length"
          :attributes="attributes"
          class="mt-5"
        />

        <ProductsOverview
          v-if="deal.overview_media.length"
          class="mt-5"
          :deal="deal"
        />
        <!-- jw: this does not include the pricing table. That is rendered just below outside of the
                 two columns that this is rendered within.
        -->
        <DealTermsAndFeatures
          v-if="isActiveDeal"
          class="mt-5"
          :deal="deal"
        />
      </section>

      <section class="d-none d-lg-block col-lg-5 col-xl-4">
        <div
          class="pdp-purchase-section"
          :class="{'interaction-active': showWishlists || showShareModal }"
        >
          <div
            v-if="showShareModal || showWishlists"
            class="pdp-interaction-container"
          >
            <ShareModal
              v-if="showShareModal"
              class="mr-3"
              :shareable-text="shareableLink"
              :title="deal.public_name"
              :subheader="deal.products?.[0].story?.subheader"
              wrapper-class="modal-wishlists"
              icon-class="dark-mode mr-1"
              label-text="Or copy link"
              :params="socialParams"
            />
            <WishlistsButton
              v-if="showWishlists"
              :deal-id="deal.id"
              :deals-count="wishlistsCount"
            />
          </div>
          <PDPPricingCard
            :deal="deal"
            :buy-button="buyButton"
            :plan-type-text="sentenceCase(planTypeText)"
            :subheader="story.subheader"
            :plans="deal.plans"
            :user="user"
            id-suffix="02"
            :is-pricing-card-email-submitted="isPricingCardEmailSubmitted"
            :banner-details="deal.banner_details"
            is-desktop
            @email-submitted="onEmailSubmitted"
          />
        </div>
      </section>
    </div>
    <!-- jw: this is not intuitive, but this is the second half of the DealTermsAndFeatures above
              but it needs to be rendered full width for desktop which is why it is rendered outside
              of the two column UI from above.
    -->
    <PricingTable
      v-if="isActiveDeal"
      :deal="deal"
    />
  </div>
</template>

<script>
import get from 'lodash/get';
import { ATTRIBUTE_TYPE, BLACKLIST_ATTRIBUTE } from '@/constants/global';
// TODO:BRO-1276: import DTacoRating from '@appsumo/dorado-nuxt';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import TacoRating from '../utilities/TacoRating.vue';
import ProductsMainImage from './carousel/ProductsMainImage.vue';
import PDPPricingCard from './PDPPricingCard.vue';
import PDPTldr from './PDPTldr.vue';
import ProductsAtGlance from '../products/at-a-glance/ProductsAtGlance.vue';
import DealTermsAndFeatures from '../products/terms-and-features/DealTermsAndFeatures.vue';
import ListingTypeLogo from './ListingTypeLogo.vue';
import { LISTING_TYPES_WITH_LOGO } from '~/constants/listing-types';
import WishlistsButton from '../wishlists/Button.vue';
import ShareModal from '../common/ShareModal.vue';
import { getUtmParamsForSocialShare } from '../../util/global';
import ProductsOverview from '../products/overview/ProductsOverview.vue';
import PricingTable from '../products/pricing-table/PricingTable.vue';

export default {
  LISTING_TYPES_WITH_LOGO,
  components: {
    ProductsMainImage,
    PDPPricingCard,
    PDPTldr,
    TacoRating,
    DealTermsAndFeatures,
    ListingTypeLogo,
    WishlistsButton,
    ShareModal,
    ProductsAtGlance,
    ProductsOverview,
    PricingTable,
    FontAwesomeIcon,
  },
  props: {
    deal: {
      type: Object,
      required: true,
    },
    // TODO temp user object to be replaced by this.$store.state.session.user
    user: {
      type: Object,
      required: true,
    },
  },
  async fetch() {
    this.showShareModal = await this.$experiments.enroll('pdp-show-share-modal') === 'enabled';
  },
  data() {
    return {
      isPricingCardEmailSubmitted: false,
      showShareModal: false,
    };
  },
  computed: {
    hasListingTypeLogo() {
      return !!this.$options.LISTING_TYPES_WITH_LOGO.includes(this.deal.listing_type);
    },
    story() {
      return get(this, ['deal', 'products', 0, 'story'], {});
    },
    buyButton() {
      return this.deal.buy_button || {};
    },
    images() {
      if (this.deal.media_url) {
        return [
          {
            alt_text: 'Featured image',
            content: '',
            description: '',
            image: this.deal.media_url,
          },
          ...this.story?.images || [],
        ];
      }
      return this.story?.images || [];
    },
    planTypeText() {
      let textStr = '';
      for (let i = 0; i < this.deal.unique_plan_types.length; i += 1) {
        const typeArr = this.deal.unique_plan_types[i];
        if (typeArr[0] === 'One Time Purchase') {
          textStr += 'Lifetime deal';
        } else if (typeArr[0] === 'Limited License') {
          textStr += `${typeArr[2]} ${typeArr[1]} DEAL`;
        } else {
          textStr += typeArr[0];
        }
        if (i < this.deal.unique_plan_types.length - 1) {
          textStr += ' & ';
        }
      }
      return textStr;
    },
    isActiveDeal() {
      return !this.deal.stage_details.is_inactive_indefinitely;
    },
    showTldr() {
      return !!this.story?.highlights?.length && !this.deal.is_info;
    },
    currentUser() {
      return this.$store.state.session.user;
    },
    wishlistsCount() {
      return this.$store.getters['wishlists/wishlistsCount'];
    },
    wishlistExperiment() {
      return this.$store.getters['wishlists/wishlistExperiment'];
    },
    showWishlists() {
      return !!(this.currentUser && this.wishlistExperiment === 'enabled' && !this.deal.is_info);
    },
    attributes() {
      return this.deal
        .attributes?.filter((attribute) => {
          if (BLACKLIST_ATTRIBUTE.includes(attribute.slug)) {
            return false;
          }

          // for boolean types filter only ones with values: true
          return !(attribute.type === ATTRIBUTE_TYPE.BOOLEAN && !attribute.values);
        });
    },
    socialParams() {
      return getUtmParamsForSocialShare(this.deal, this.currentUser?.id);
    },
    shareableLink() {
      if (process.client) {
        return `${window.location.origin}${this.deal.get_absolute_url}`;
      }

      return '';
    },
  },
  methods: {
    sentenceCase(str) {
      return str.toLowerCase().replace(/(^\s*\w|[.!?]\s*\w)/g, (c) => c.toUpperCase());
    },
    onEmailSubmitted() {
      this.isPricingCardEmailSubmitted = true;
    },
  },
};
</script>

<style>
  /*  react to navbar scroll behavior (.navbar-visible is attached to body) */
  .navbar-visible .pdp-purchase-section {
    top: calc(166px + var(--banner-height, 0px)) !important;
  }

  /* Prevent overflow issues (.overflow-over-navigation is attached to body) */

  /* purgecss ignore */
  .overflow-over-navigation .pdp-purchase-section {
    z-index: 3000 !important;
  }

  /* stylelint-disable-next-line selector-class-pattern */
  .with_fixed_header.navbar-visible .pdp-purchase-section {
    top: calc(216px + var(--banner-height, 0px)) !important;
  }
</style>

<style scoped lang="scss">
  @import '~/styles/dorado/themes/dorado';

  // Set custom styles for share modal
  div ::v-deep {
    .mobile-share-icon-container {
      background-color: $gray-100;
      border-radius: 50%;
      height: 40px;
      padding: 9px 12px;
      width: 40px;
    }

    .dark-mode path {
      fill: $forest;
    }
  }

  /* stylelint-disable selector-no-qualifying-type */
  .pdp-story-container {
    --taco-height: 24px;
    --taco-width: 114px;

    padding-top: 32px;
  }

  .pdp-description ::v-deep b {
    font-weight: 700;
  }

  .pdp-interaction-container {
    display: flex;
    flex: 1;
    height: auto;
    justify-content: end;
    margin-bottom: 1rem;
  }

  .listing-type-logo-container {
    flex: 0;
  }

  @media (min-width: 992px) {
    .listing-type-logo-container {
      display: none !important;
    }
  }

  .pdp-product-subtitle {
    font-family: Roboto, Verdana, sans-serif;
    font-weight: 400 !important;
  }

  .pdp-purchase-section {
    position: sticky;
    // Setting top dynamically if top banner is present otherwise use 0px by default.
    // See static/js/banner.js
    top: calc(86px + var(--banner-height, 0px));
    transition: top 0.4s ease-in-out;
    z-index: 1020;
    margin-bottom: 16px;

    &.interaction-active {
      // combination of the height of icon and margin
      // between interaction container and pdp price card
      margin-top: -2.25rem;
    }
  }

  h1.pdp-page-title {
    margin: 0;
  }

  p.pdp-plan-types {
    margin-top: 10px;
    font-weight: 600;
    font-size: 14px;
    line-height: 21px;
    color: $grace;
    text-transform: lowercase;
  }

  p.pdp-plan-types::first-letter {
    text-transform: capitalize;
  }

  // TODO:BRO-1276: Remove this custom CSS
  // Dorado alert styles - Overrides legacy ones
  .alert {
    align-items: flex-start;
    border: 1px solid transparent;
    border-radius: 0;
    display: flex;
    padding: 1rem;
    position: relative;

    &.alert-warning {
      color: $dark;
      background-color: $warning;
      border-color: $warning;
      font-size: 0.875rem;
    }

    .alert-icon {
      font-size: 24px;
      margin: auto 0;
    }
  }

  @media (max-width: 991px) {
    section.pdp-purchase-section {
      position: relative !important;
      margin-top: 0;
      margin-bottom: 20px;
    }

    .pdp-interaction-container {
      height: 1.25rem;
    }

    .icon-container-spacing {
      padding-bottom: 1rem;
    }
  }
  /* stylelint-enable selector-no-qualifying-type */
</style>
